import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './UtilizationGauge.module.scss';
import { NullableNumber } from '@/src/types/common';

const ARIA_LABEL_KEY = 'labDashboard.utilizationSummary.ariaLabel';

export interface UtilizationGaugeProps {
  /** Utilization percentage value. Clamped to [0, 100]. Defaults to 0. */
  value?: NullableNumber;
}

// SVG geometry constants
const CENTER_X = 100;
const CENTER_Y = 100;
const RADIUS = 80;
const STROKE_WIDTH = 16;
const HALF_CIRCUMFERENCE = Math.PI * RADIUS; // ≈ 251.33

// Semi-circle arc path: starts at the left endpoint, arcs over the top to the right endpoint
const ARC_PATH = `M ${CENTER_X - RADIUS} ${CENTER_Y} A ${RADIUS} ${RADIUS} 0 0 1 ${CENTER_X + RADIUS} ${CENTER_Y}`;

// Color thresholds and values for the gauge fill
const COLOR_GREEN = { r: 29, g: 148, b: 73 }; // #1d9449
const COLOR_YELLOW = { r: 204, g: 148, b: 0 }; // green channel matches green stop for monotonic transition
const COLOR_ORANGE = { r: 236, g: 114, b: 17 }; // #ec7211
const COLOR_RED = { r: 214, g: 2, b: 18 }; // #d60212

/**
 * Clamps a raw value to [0, 100].
 * Returns 0 for undefined, null, NaN, or non-numeric inputs.
 */
export const clampValue = (raw: unknown): number => {
  if (raw === null || raw === undefined || typeof raw !== 'number' || Number.isNaN(raw)) {
    return 0;
  }
  return Math.min(100, Math.max(0, raw));
};

/**
 * Formats a clamped value to one decimal place with a % suffix.
 */
export const formatDisplayValue = (clamped: number): string => {
  return `${clamped.toFixed(1)}%`;
};

/**
 * Computes the gauge fill color based on the clamped value.
 *
 * - 0% to 50%: solid green
 * - 50% to 60%: gradient from green to yellow
 * - 60% to 80%: gradient from yellow to orange
 * - 80% to 90%: gradient from orange to red
 * - 90% to 100%: solid red
 */
export const getGaugeColor = (clamped: number): string => {
  if (clamped <= 50) {
    return `rgb(${COLOR_GREEN.r}, ${COLOR_GREEN.g}, ${COLOR_GREEN.b})`;
  }
  if (clamped <= 60) {
    const t = (clamped - 50) / 10;
    const r = Math.round(COLOR_GREEN.r + t * (COLOR_YELLOW.r - COLOR_GREEN.r));
    const g = Math.floor(COLOR_GREEN.g + t * (COLOR_YELLOW.g - COLOR_GREEN.g));
    const b = Math.round(COLOR_GREEN.b + t * (COLOR_YELLOW.b - COLOR_GREEN.b));
    return `rgb(${r}, ${g}, ${b})`;
  }
  if (clamped <= 80) {
    const t = (clamped - 60) / 20;
    const r = Math.round(COLOR_YELLOW.r + t * (COLOR_ORANGE.r - COLOR_YELLOW.r));
    const g = Math.floor(COLOR_YELLOW.g + t * (COLOR_ORANGE.g - COLOR_YELLOW.g));
    const b = Math.round(COLOR_YELLOW.b + t * (COLOR_ORANGE.b - COLOR_YELLOW.b));
    return `rgb(${r}, ${g}, ${b})`;
  }
  if (clamped <= 90) {
    const t = (clamped - 80) / 10;
    const r = Math.round(COLOR_ORANGE.r + t * (COLOR_RED.r - COLOR_ORANGE.r));
    const g = Math.floor(COLOR_ORANGE.g + t * (COLOR_RED.g - COLOR_ORANGE.g));
    const b = Math.round(COLOR_ORANGE.b + t * (COLOR_RED.b - COLOR_ORANGE.b));
    return `rgb(${r}, ${g}, ${b})`;
  }
  return `rgb(${COLOR_RED.r}, ${COLOR_RED.g}, ${COLOR_RED.b})`;
};

const UtilizationGauge: React.FC<UtilizationGaugeProps> = ({ value }) => {
  const { t } = useTranslation();

  const clamped = clampValue(value);
  const displayValue = formatDisplayValue(clamped);
  const fillLength = (clamped / 100) * HALF_CIRCUMFERENCE;
  const gaugeColor = getGaugeColor(clamped);

  return (
    <svg
      className={styles.gauge}
      viewBox="0 0 200 120"
      width="100%"
      role="img"
      aria-label={t(ARIA_LABEL_KEY, { value: clamped.toFixed(1) })}
    >
      {/* Arc Track - full semi-circle background */}
      <path className={styles.arcTrack} d={ARC_PATH} fill="none" strokeWidth={STROKE_WIDTH} strokeLinecap="round" />
      {/* Arc Fill - proportional to clamped value, color based on value threshold */}
      {clamped > 0 && (
        <path
          className={styles.arcFill}
          d={ARC_PATH}
          fill="none"
          stroke={gaugeColor}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={`${fillLength} ${HALF_CIRCUMFERENCE}`}
          strokeLinecap="round"
        />
      )}
      {/* Value Label - centered text */}
      <text className={styles.valueLabel} x={CENTER_X} y={CENTER_Y + 10} textAnchor="middle" dominantBaseline="middle">
        {displayValue}
      </text>
    </svg>
  );
};

export default UtilizationGauge;
